import { useCallback, useState } from "react";

import { useNavigate, useParams } from "@tanstack/react-router";
import { Alert, Card, Divider, Flex, Spin, Typography } from "antd";

import {
	canSaveEntityFromUserStore,
	isPbmPortalClient,
	useAPI,
	useNeuClient,
} from "@neu/libui";

import { PopupForm } from "#components";
import { calcAge, fmt, fmtDate, initials, phone } from "#utils";

import styles from "./PatientSnapshot.module.scss";
import {
	caseColumns,
	getEnrollmentRows,
	getHeaderMeta,
	getMedicalCards,
	getPlanDetailsRows,
} from "./patientSnapshotData";
import {
	Field,
	LabelValueCard,
	SnapshotHeader,
	TableCard,
} from "./snapshotcomponents";

interface PapRenewalItem {
	patientPapId: number;
	patientCaseId: number | null;
	shortName: string | null;
	programName: string | null;
	programId: number | null;
	renewalDate: string;
}

/* ================================================================== */
/*  Component                                                          */
/* ================================================================== */

export const PatientSnapshot = () => {
	const navigate = useNavigate();
	const neuClient = useNeuClient();
	const isPbmPortal = isPbmPortalClient(neuClient);
	const { patientID } = useParams({ strict: false });
	const [{ data: snapshot, isLoading, refetch }] = useAPI(
		patientID ? `/patient/snapshot?id=${patientID}` : null
	);

	const [openForm, setOpenForm] = useState(false);
	const canEditPatient = canSaveEntityFromUserStore("patient");

	const handleCaseClick = useCallback(
		(caseRow: Record<string, unknown>) => {
			const caseID = caseRow.id;
			if (
				!patientID ||
				(typeof caseID !== "string" && typeof caseID !== "number")
			) {
				return;
			}
			const patientBasePath = isPbmPortal
				? `/pbm-portal/patient/${patientID}`
				: `/patient/${patientID}`;
			void navigate({
				to: `${patientBasePath}/case` as "/",
				search: {
					mode: "read",
					id: String(caseID),
				} as Record<string, string>,
			});
		},
		[navigate, patientID, isPbmPortal]
	);

	const root = (snapshot ?? {}) as Record<string, any>;
	const patientData = (root.patient ?? root) as Record<string, any>;
	const patientCases = Array.isArray(root.patient_case)
		? root.patient_case
		: Array.isArray(root.patientCase)
			? root.patientCase
			: Array.isArray(root.cases)
				? root.cases
				: [];
	const papRenewalsRaw = root.pap_renewals ?? root.papRenewals;
	const papRenewals: PapRenewalItem[] = Array.isArray(papRenewalsRaw)
		? (papRenewalsRaw as PapRenewalItem[])
		: [];
	const homeStateRaw = patientData.home_state;
	const homeStateLabel =
		homeStateRaw &&
		typeof homeStateRaw === "object" &&
		"code" in homeStateRaw
			? (homeStateRaw as { code?: string }).code
			: homeStateRaw;
	const address = [
		patientData.home_street,
		patientData.home_city,
		homeStateLabel,
		patientData.home_zip,
	]
		.filter(Boolean)
		.join(", ");

	return (
		<Flex vertical className={styles.snapshot}>
			{(isLoading || !snapshot || !patientData.id) && (
				<Flex
					flex={1}
					justify="center"
					align="center"
					className={styles.loadingWrap}
				>
					<Spin size="large" />
				</Flex>
			)}

			{!isLoading && snapshot && patientData.id && (
				<>
					<SnapshotHeader
						className={styles.profileHeader}
						avatar={initials(
							patientData.firstname,
							patientData.lastname
						)}
						avatarClassName={styles.avatar}
						title={`${fmt(patientData.firstname)} ${fmt(patientData.lastname)}`}
						statusLabel={fmt(patientData.patient_status, "Unknown")}
						statusColor={
							patientData.patient_status === "Active"
								? "success"
								: "default"
						}
						infoClassName={styles.profileInfo}
						nameClassName={styles.profileName}
						metaClassName={styles.profileMeta}
						actionsClassName={styles.profileActions}
						meta={getHeaderMeta(patientData)}
						actions={[
							{
								label: canEditPatient ? "Edit" : "View",
								type: "primary",
								onClick: () => {
									setOpenForm(true);
								},
							},
						]}
					/>
					{papRenewals.length > 0 && (
						<Flex
							vertical
							gap="small"
							className={styles.renewalBanners}
						>
							{papRenewals.map((item) => {
								const drugLabel =
									item.shortName && item.shortName !== "None"
										? item.shortName
										: item.programName || "PAP";
								return (
									<Alert
										key={item.patientPapId}
										type="warning"
										showIcon
										className={styles.renewalBanner}
										title={
											<Typography.Text>
												PAP Renewal Pending
												{item.renewalDate
													? ` – Expires On: ${fmtDate(item.renewalDate)}`
													: ""}
												{" · "}
												<Typography.Text strong>
													{drugLabel}
												</Typography.Text>
											</Typography.Text>
										}
									/>
								);
							})}
						</Flex>
					)}
					<PopupForm
						open={openForm}
						entityName="patient"
						mode={canEditPatient ? "edit" : "read"}
						recordID={patientData.id}
						onClose={() => {
							setOpenForm(false);
							void refetch();
						}}
					/>

					<Flex className={styles.middleGrid}>
						<Card
							title="Demographics & Contact"
							size="small"
							className={styles.demoContactCard}
						>
							<Flex className={styles.demoGrid}>
								<Field
									label="DATE OF BIRTH"
									value={fmtDate(patientData.dob)}
								/>
								<Field
									label="AGE / SEX"
									value={`${calcAge(patientData.dob)} / ${fmt(patientData.gender)}`}
								/>
								<Field
									label="MEMBER ID"
									value={fmt(patientData.external_id)}
								/>
								<Field
									label="SSN"
									value={
										patientData.ssn_4
											? `***-**-${String(patientData.ssn_4)}`
											: "—"
									}
								/>
								<Divider className={styles.gridDivider} />
								<Field
									label="CELL PHONE"
									value={phone(patientData.phone_cell)}
								/>
								<Field
									label="HOME PHONE"
									value={phone(patientData.phone_home)}
								/>
								<Field
									label="EMAIL"
									value={fmt(patientData.email)}
									span
								/>
								<Field
									label="ADDRESS"
									value={address || "—"}
									span
								/>
								<Divider className={styles.gridDivider} />
								<Field
									label="ALERT NOTES"
									value={fmt(patientData.alert_notes)}
									span
									tone="alert"
								/>
							</Flex>
						</Card>
						<LabelValueCard
							title="Plan Details"
							className={styles.planCard}
							rowClassName={styles.planRow}
							labelClassName={styles.planLabel}
							valueClassName={styles.planValue}
							rows={getPlanDetailsRows(patientData)}
						/>

						<LabelValueCard
							title="Enrollment Status"
							className={styles.checklistCard}
							rowClassName={styles.planRow}
							labelClassName={styles.planLabel}
							valueClassName={styles.planValue}
							rows={getEnrollmentRows(patientData)}
						/>
					</Flex>

					<div className={styles.bottomSection}>
						<TableCard
							title="Active Cases"
							className={styles.casesCard}
							columns={caseColumns}
							dataSource={patientCases}
							rowKey="id"
							emptyText="No active cases"
							onRowClick={handleCaseClick}
						/>

						{getMedicalCards(patientData).map((card) => (
							<LabelValueCard
								key={card.title}
								title={card.title}
								className={styles.medicalCard}
								rowClassName={styles.detailRow}
								labelClassName={styles.detailLabel}
								valueClassName={styles.detailValue}
								rows={card.rows}
							/>
						))}
					</div>
				</>
			)}
		</Flex>
	);
};
